import React, {
  useState,
  useContext,
  createRef,
  useEffect,
  Fragment,
} from "react";
import { Link, useHistory } from "react-router-dom";
import {
  faUserCircle,
  faBars,
  faFolder,
  faUsers
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { shape, string } from "prop-types";

import NotificationContext from "../../contexts/NotificationsContext";
import AppDivisionContext from "../../contexts/AppDivisionContext";
import AppContext from '../../contexts/AppContext';
import AuthenticationContext from '../../contexts/AuthenticationContext';
import ModalNav from "./ModalNav";
import NotificationBell from "../Shared/Notifications/NotificationBell";
import NotificationPopper from "../Shared/Notifications/NotificationPopper";
import UserPopper from "./poppers/UserPopper";
import DivisionPopper from "./poppers/DivisionPopper";
import TenantPopper from './poppers/TenantPopper';
import MFAVerificationModal from '../Shared/MFAVerificationModal';
import { getRootProperties } from "./SecondaryNavigation";
import tenantApi from '../../utils/api/tenant';
import userApi from '../../utils/api/user';
import { pendingUploadsWarning } from '../../utils/backgroundPhotoUploads';
import { useLogo } from "../../contexts/AppLogoContext";

/**
 * Default application layout, provides a sidebar and content area
 *
 * User should be logged in
 */

const PopperMenu = ({ type, ...props }) => {
  switch (type) {
    case "user":
      return <UserPopper {...props} />;
      case 'tenant':
      return <TenantPopper {...props} />;
    case "notification":
      return <NotificationPopper {...props} />;
    case "division":
      return <DivisionPopper {...props} />;
    default:
      return "";
  }
};

const HamburgerBar = props => {
  const [menuType, setMenuType] = useState("user"); // oneOf: ['user', 'notification', 'division']
  const [location, setLocation] = useState(null);
  const [open, setOpen] = useState(false);
  const [coordinates, setCoordinates] = useState({ top: null, right: null });
  const [showPopper, setPopperVis] = useState(false);
  const [showMFAModal, setShowMFAModal] = useState(false);
  const [mfaData, setMfaData] = useState(null);

  const history = useHistory();
  const notificationRef = createRef();
  const userRef = createRef();
  const divisionRef = createRef();
  const tenantRef = createRef();
  const notificationContext = useContext(NotificationContext);
  const appDivisionContext = useContext(AppDivisionContext);
  const appContext = useContext(AppContext);
  const authStore = useContext(AuthenticationContext);

  const { appLogo } = useLogo();

  const notificationCount = notificationContext.notifications.filter(
    ({ is_opened }) => !is_opened,
  ).length;
  const user = appContext.get('user');

  useEffect(() => {
    if (location !== props.match.path) {
      setOpen(false);
    }
    setLocation(props.match.path);
  }, [props.match.path]);

  /**
   *
   * @param {string} type `user` `notif`
   */

  const getCoordinates = type => {
    switch (type) {
      case "user":
        return userRef.current.getBoundingClientRect();
      case "tenant":
        return tenantRef.current.getBoundingClientRect();
      case "notification":
        return notificationRef.current.getBoundingClientRect();
      case "division":
        return divisionRef.current.getBoundingClientRect();
      default:
        return userRef.current.getBoundingClientRect();
    }
  };

  const getDivision = () => {
    const division = appDivisionContext.divisions.find(
      div => div.id === parseInt(appDivisionContext.appDivisionId),
    );

    return division ? division.name : null;
  };

  function handleClick(type) {
    const { right, bottom } = getCoordinates(type);

    setCoordinates({ right: window.innerWidth - right, top: bottom });
    setMenuType(type);
    setPopperVis(true);
  }

  const onDivisionChange = (division = appDivisionContext.appDivisionId) => {
    const { rootTo } = getRootProperties(props.match);

    history.push(appDivisionContext.getPath(rootTo, division));
  };

  const handleMFARequired = (data) => {
    setMfaData(data);
    setShowMFAModal(true);
  };

  const handleMFAVerificationSuccess = async () => {
    if (mfaData) {
      const { tenantId, tenantName } = mfaData;

      // SW2-2458: checked again here, not only when the switch was started. A
      // save that finished while this dialog was open may have queued photos
      // since, and changeTenant moves the session before the page navigates.
      if (pendingUploadsWarning('switching company')) {
        setShowMFAModal(false);

        return;
      }

      try {
        // Call changeTenant again after successful MFA verification to get the token
        await userApi.changeTenant(tenantId);
        
        const startUrl = process.env.REACT_APP_CLIENT_BASE_URL;

        if (startUrl && tenantName) {
          const url = startUrl.split('//');

          if (url) {
            window.location.href =
              url[0] +
              `//${tenantName}.${url[1]}`;
          }
        }
      } catch (error) {
        console.error('Error changing tenant after MFA verification:', error);
      }
    }
  };

  const handleMFAModalClose = () => {
    setShowMFAModal(false);
    setMfaData(null);
  };

  return (
    <Fragment>
      <header className="hamburger-nav-bar">
        <FontAwesomeIcon
          icon={faBars}
          className="nav-icon pointer"
          onClick={() => setOpen(true)}
          title="Open menu"
        />

        <Link to={`/dashboard`} className="home-link" title="Home">
          <img src={appLogo} alt="logo" className="nav-icon" />
        </Link>

        <div>
        {user && user.tenants && user.tenants.length > 1 && (
            <span
              onClick={() => handleClick('tenant')}
              className="inline-nav pad-right pointer"
              ref={tenantRef}
            >
              <FontAwesomeIcon icon={faUsers}  className="nav-icon" />
            </span>
          )}
          {getDivision() && !appDivisionContext.disableSwitcher && (
            <span
              onClick={() => handleClick("division")}
              className="inline-nav pad-right pointer"
              ref={divisionRef}
            >
              <FontAwesomeIcon icon={faFolder} className="nav-icon" />
            </span>
          )}

          <NotificationBell
            handleClick={() => handleClick("notification")}
            notificationCount={notificationCount}
            className="nav-icon"
            ref={notificationRef}
            title="Notifications"
          />

          <span
            onClick={() => handleClick("user")}
            className="inline-nav pointer"
            ref={userRef}
          >
            <FontAwesomeIcon icon={faUserCircle} className="nav-icon" />
          </span>
        </div>
      </header>

      <ModalNav
        open={open}
        handleClose={() => setOpen(false)}
        match={props.match}
      />

      {showPopper && (
        <PopperMenu
          type={menuType}
          coordinates={coordinates}
          handleClose={() => setPopperVis(false)}
          onChange={menuType === "division" ? onDivisionChange : null}
          onMFARequired={menuType === "tenant" ? handleMFARequired : null}
        />
      )}

      {mfaData && (
        <MFAVerificationModal
          show={showMFAModal}
          handleClose={handleMFAModalClose}
          onVerificationSuccess={handleMFAVerificationSuccess}
          email={mfaData.email}
          sendVerificationCode={authStore.sendVerificationCode}
          verifyCode={authStore.verifyCode}
        />
      )}
    </Fragment>
  );
};

HamburgerBar.propTypes = {
  match: shape({
    path: string.isRequired,
  }).isRequired,
};

export default HamburgerBar;
